import React, { useState } from "react";
import {
  Thermometer,
  Lightbulb,
  Music,
  Heart,
  Sun,
  Moon,
  Waves,
  Sparkles,
  Volume2,
} from "lucide-react";

interface ScenePreset {
  id: string;
  name: string;
  icon: React.ReactNode;
  temperature: number;
  lightColor: string;
  music: string;
}

const PatientSceneSetter: React.FC = () => {
  const [temperature, setTemperature] = useState(72);
  const [lightColor, setLightColor] = useState("#FFE5B4");
  const [selectedMusic, setSelectedMusic] = useState("Nature Sounds");
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const scenePresets: ScenePreset[] = [
    {
      id: "relaxing",
      name: "Relaxing",
      icon: <Waves className="w-5 h-5" />,
      temperature: 72,
      lightColor: "#B4D4FF",
      music: "Ocean Waves",
    },
    {
      id: "energizing",
      name: "Energizing",
      icon: <Sun className="w-5 h-5" />,
      temperature: 70,
      lightColor: "#FFEB99",
      music: "Uplifting Melodies",
    },
    {
      id: "sleep",
      name: "Sleep",
      icon: <Moon className="w-5 h-5" />,
      temperature: 68,
      lightColor: "#E6D5FF",
      music: "Sleep Sounds",
    },
    {
      id: "comfort",
      name: "Comfort",
      icon: <Heart className="w-5 h-5" />,
      temperature: 74,
      lightColor: "#FFE5E5",
      music: "Gentle Piano",
    },
  ];

  const colorOptions = [
    { name: "Warm White", color: "#FFE5B4" },
    { name: "Cool Blue", color: "#B4D4FF" },
    { name: "Soft Purple", color: "#E6D5FF" },
    { name: "Gentle Green", color: "#D4FFB4" },
    { name: "Sunrise", color: "#FFEB99" },
    { name: "Rose", color: "#FFE5E5" },
  ];

  const musicOptions = [
    "Nature Sounds",
    "Ocean Waves",
    "Gentle Piano",
    "Classical Music",
    "Uplifting Melodies",
    "Sleep Sounds",
    "Meditation Music",
    "Your Playlist",
  ];

  const applyPreset = (preset: ScenePreset) => {
    setTemperature(preset.temperature);
    setLightColor(preset.lightColor);
    setSelectedMusic(preset.music);
    setActivePreset(preset.id);
  };

  const handleCustomChange = () => {
    setActivePreset(null);
  };

  return (
    <div className="flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-black rounded-3xl p-8 shadow-2xl max-w-4xl w-full">
        <div className="bg-white rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Your Comfort Settings
            </h1>
            <p className="text-gray-600">
              Tap a scene or customize your room environment
            </p>
          </div>

          {/* Quick Scene Presets */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Quick Scenes
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {scenePresets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(preset)}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                    activePreset === preset.id
                      ? "border-blue-500 bg-blue-50 shadow-lg scale-105"
                      : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`p-3 rounded-full ${
                        activePreset === preset.id
                          ? "bg-blue-100"
                          : "bg-gray-100"
                      }`}
                    >
                      {preset.icon}
                    </div>
                    <span className="font-medium text-gray-700">
                      {preset.name}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Controls */}
          <div className="space-y-6">
            {/* Temperature Control */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Thermometer className="w-5 h-5 text-red-500" />
                Temperature
              </h3>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold text-gray-800 w-20">
                  {temperature}°F
                </span>
                <input
                  type="range"
                  min="65"
                  max="80"
                  value={temperature}
                  onChange={(e) => {
                    setTemperature(Number(e.target.value));
                    handleCustomChange();
                  }}
                  className="flex-1 h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${
                      ((temperature - 65) / 15) * 100
                    }%, #E5E7EB ${
                      ((temperature - 65) / 15) * 100
                    }%, #E5E7EB 100%)`,
                  }}
                />
                <div className="flex gap-2">
                  <span className="text-sm text-gray-500">Cool</span>
                  <span className="text-sm text-gray-500">Warm</span>
                </div>
              </div>
            </div>

            {/* Lighting Control */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Lighting Color
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {colorOptions.map((option) => (
                  <button
                    key={option.color}
                    onClick={() => {
                      setLightColor(option.color);
                      handleCustomChange();
                    }}
                    className={`relative p-4 rounded-lg transition-all duration-300 ${
                      lightColor === option.color
                        ? "ring-4 ring-blue-500 shadow-lg scale-105"
                        : "hover:shadow-md"
                    }`}
                    style={{ backgroundColor: option.color }}
                  >
                    <div className="text-xs text-gray-700 font-medium text-center mt-8">
                      {option.name}
                    </div>
                    {lightColor === option.color && (
                      <div className="absolute top-1 right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Music Control */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <Music className="w-5 h-5 text-green-500" />
                Music & Sounds
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {musicOptions.map((music) => (
                  <button
                    key={music}
                    onClick={() => {
                      setSelectedMusic(music);
                      handleCustomChange();
                    }}
                    className={`p-3 rounded-lg border-2 transition-all duration-300 flex items-center gap-2 ${
                      selectedMusic === music
                        ? "border-green-500 bg-green-50 shadow-md"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <Volume2 className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {music}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Current Settings Display */}
          <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">
              Current Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Thermometer className="w-4 h-4 text-gray-600" />
                <span className="text-gray-600">Temperature:</span>
                <span className="font-semibold text-gray-800">
                  {temperature}°F
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full border border-gray-300"
                  style={{ backgroundColor: lightColor }}
                />
                <span className="text-gray-600">Lighting:</span>
                <span className="font-semibold text-gray-800">
                  {colorOptions.find((c) => c.color === lightColor)?.name ||
                    "Custom"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Music className="w-4 h-4 text-gray-600" />
                <span className="text-gray-600">Music:</span>
                <span className="font-semibold text-gray-800">
                  {selectedMusic}
                </span>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6 flex justify-center">
            <button className="px-8 py-3 bg-blue-500 text-white font-semibold rounded-full hover:bg-blue-600 transition-colors duration-300 shadow-lg hover:shadow-xl">
              Save My Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientSceneSetter;
